import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Alert, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { PageHeader } from '../../components/ui/PageHeader';
import { DataTable } from '../../components/common/DataTable';
import { ModuleColumn } from '../../types';
import { biltyItemDescriptionAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { brandColors, RADIUS, brandCss } from '../../theme/appTheme';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../../theme/formStyles';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants/tableDefaults';
import { useSystemTotal } from '../../hooks/useSystemTotal';

const columns: ModuleColumn[] = [
  { field: 'name', header: 'Description' },
  { field: 'category', header: 'Category' },
  { field: 'code', header: 'Code' },
  { field: 'status', header: 'Status' },
];

const emptyForm = { name: '', category: '', code: '', status: 'active' };

export const BiltyItemDescriptionsPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const canCreate = hasPermission('bilty', 'create');
  const canView = hasPermission('bilty', 'view');
  const canEdit = hasPermission('bilty', 'edit');
  const canDelete = hasPermission('bilty', 'delete');
  const { total: systemTotal, refresh: refreshTotal } = useSystemTotal('bilty_items');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await biltyItemDescriptionAPI.getAll();
      setRows(Array.isArray(res.data) ? res.data : res.data?.data || []);
      refreshTotal();
    } catch {
      setError('Failed to load goods names');
    } finally {
      setLoading(false);
    }
  }, [refreshTotal]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (row: Record<string, unknown>) => {
    setEditId(String(row._id));
    setForm({
      name: String(row.name || ''),
      category: String(row.category || ''),
      code: String(row.code || ''),
      status: String(row.status || 'active'),
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!String(form.name).trim()) {
      setError('Description name is required');
      return;
    }
    try {
      setSaving(true);
      setError('');
      if (editId) {
        await biltyItemDescriptionAPI.update(editId, form);
        setSuccess('Description updated');
      } else {
        await biltyItemDescriptionAPI.create(form);
        setSuccess('Description created');
      }
      setModalOpen(false);
      load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Goods Names"
        subtitle={getPageSubtitle('bilty_items', 'Goods Names', { total: systemTotal })}
        icon={<Inventory2OutlinedIcon />}

        action={canCreate ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
            sx={{ px: 3, fontWeight: 700, borderRadius: `${RADIUS}px`, background: brandCss.gradientPrimary }}>
            Add Description
          </Button>
        ) : undefined}
      />

      {error && !modalOpen && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <DataTable
        columns={[
          ...columns.map((col) => (
            col.field === 'status'
              ? {
                ...col,
                render: (row: Record<string, unknown>) => (
                  <Chip
                    label={String(row.status || 'active')}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      textTransform: 'capitalize',
                      bgcolor: row.status === 'active' ? brandCss.green100 : '#fee2e2',
                      color: row.status === 'active' ? brandCss.primaryDark : '#dc2626',
                    }}
                  />
                ),
              }
              : col
          )),
          {
            field: '_actions',
            header: 'Actions',
            render: (row: Record<string, unknown>) => (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {canEdit && (
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEdit(row); }}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={async (e) => {
                      e.stopPropagation();
                      if (!window.confirm('Delete this description?')) return;
                      await biltyItemDescriptionAPI.remove(String(row._id));
                      setSuccess('Description deleted');
                      load();
                    }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ),
          },
        ]}
        rows={rows}
        loading={loading}
        search={search}
        onSearchChange={(v: string) => { setSearch(v); setPage(0); }}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        recordLabel="descriptions"
      />

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={800}>{editId ? 'Edit Description' : 'Add Description'}</DialogTitle>
        <DialogContent>
          {error && modalOpen && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Description" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Steel Coils, Cotton Bales"
              fullWidth size="small" sx={catchyTextFieldSx} />
            <TextField label="Category" value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="e.g. Metals, Textiles"
              fullWidth size="small" sx={catchyTextFieldSx} />
            <TextField label="Code" value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              fullWidth size="small" sx={catchyTextFieldSx} />
            <TextField select label="Status" value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              fullWidth size="small" sx={catchyTextFieldSx}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={catchyPrimaryButtonSx}>
            {editId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
